/**
 * Machine ID Attestation Service
 * 
 * Handles the complete attestation flow:
 * 1. Get challenge nonce from backend
 * 2. Request C++ to generate TPM signatures
 * 3. Send attestation to backend
 * 4. Store attestation metadata locally
 */

import constants from '@playcastdotio/GlobalConfig';
import * as Sentry from '@sentry/react';
import { ensureTrailingSlash } from '../modules/apiV2';

/**
 * Challenge response from backend
 */
interface ChallengeResponse {
  nonce: string;
  expiresAt: number;
}

/**
 * Attestation payload (what gets signed by TPM)
 */
interface AttestationPayload {
  machineId: string;
  clerkUserId: string;
  nonce: string;
}

/**
 * Attestation data from C++ (systemGetMachineIdAttestationMSG reply)
 * This is sent to the backend /attest endpoint
 * Note: clerkUserId is NOT sent in request body - backend extracts it from JWT token
 */
export interface AttestationData {
  machineId: string;
  aikPublicKey: string;
  seedSignature: string;
  attestationSignature: string;
  attestationPayload: AttestationPayload;
}

/**
 * Attestation result from backend
 */
interface AttestationResult {
  success: boolean;
  attestedAt?: number;
  expiresAt?: number;
  error?: string;
  message?: string;
}

/**
 * Attestation validation result (from isAttestationTentativelyValid)
 */
export interface AttestationValidation {
  isValid: boolean;
  attestedUserId?: string;
  attestedAt?: number;
  expiresAt?: number;
  reason?: 'EXPIRED' | 'USER_MISMATCH' | 'NOT_ATTESTED';
}

/**
 * Get a challenge nonce from the backend
 */
export async function getChallenge(): Promise<ChallengeResponse> {
  const url = `${ensureTrailingSlash(constants.REALTIME_API_BASE_URL)}realtime/machine-identity/challenge`;

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get challenge: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    console.log('[MachineIdAttestation] Challenge received:', {
      nonceLength: data.nonce?.length,
      expiresAt: data.expiresAt ? new Date(data.expiresAt).toISOString() : 'unknown',
    });

    return data;
  } catch (error) {
    console.error('[MachineIdAttestation] Failed to get challenge:', error);
    Sentry.captureException(error, {
      tags: {
        service: 'machineIdAttestation',
        operation: 'getChallenge',
      },
    });
    throw error;
  }
}

/**
 * Canonicalize an attestation payload exactly as the realtime-api server does
 * (`canonicalJson` in `managers/realtime/attestation.ts`): recursively sort
 * object keys and serialize without whitespace. Kept in lock-step with the
 * backend so local verification reproduces the precise bytes the server signs
 * over — for the current `{ clerkUserId, machineId, nonce }` payload and for
 * legacy payloads that still include a `timestamp` field.
 *
 * Freshness note: for legacy attestations the `timestamp` IS part of the signed
 * bytes (so mutating it invalidates the signature), but it is NOT used to
 * determine freshness. Replay/freshness is governed solely by the single-use,
 * server-issued nonce; the client clock is never trusted. New clients omit
 * `timestamp` entirely and sign only `{ clerkUserId, machineId, nonce }`.
 *
 * Exported for unit testing of the legacy-vs-new canonicalization contract.
 */
export function canonicalizeAttestationPayload(value: unknown): string {
  const sortKeys = (input: unknown): unknown => {
    if (Array.isArray(input)) {
      return input.map(sortKeys);
    }
    if (input !== null && typeof input === 'object') {
      return Object.keys(input as Record<string, unknown>)
        .sort()
        .reduce<Record<string, unknown>>((acc, key) => {
          acc[key] = sortKeys((input as Record<string, unknown>)[key]);
          return acc;
        }, {});
    }
    return input;
  };

  return JSON.stringify(sortKeys(value));
}

/**
 * TEMP DIAGNOSTIC (#12941): verify the attestation signatures locally with Web Crypto
 * using the same algorithm/format the realtime-api server uses. If this returns
 * { seedValid: true, payloadValid: true } but the server still rejects with
 * INVALID_SEED_SIGNATURE / INVALID_ATTESTATION_SIGNATURE, the deployed server
 * is the problem (stale build / wrong env). If this returns false locally, the
 * data the renderer is about to send is itself bad.
 */
async function diagnosticVerifyAttestationLocally(
  attestationData: AttestationData,
): Promise<{ seedValid: boolean; payloadValid: boolean; reason?: string }> {
  try {
    // Explicit Uint8Array<ArrayBuffer> typing: TS 5.7+ defaults Uint8Array to
    // <ArrayBufferLike>, which SubtleCrypto's BufferSource param rejects (it
    // wants <ArrayBuffer> specifically). Annotating as Uint8Array<ArrayBuffer>
    // pins the backing-buffer kind so the verify() calls type-check.
    const b64ToBytes = (b64: string): Uint8Array<ArrayBuffer> => {
      const bin = atob(b64);
      const buf = new ArrayBuffer(bin.length);
      const out = new Uint8Array(buf);
      for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
      return out;
    };
    const bytesToB64Url = (bytes: Uint8Array<ArrayBuffer>): string =>
      btoa(String.fromCharCode(...bytes))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');

    const pubKeyBuf = b64ToBytes(attestationData.aikPublicKey);
    if (pubKeyBuf.length < 8) {
      return { seedValid: false, payloadValid: false, reason: 'aikPublicKey too short' };
    }
    const dv = new DataView(pubKeyBuf.buffer, pubKeyBuf.byteOffset, pubKeyBuf.byteLength);
    const magic = dv.getUint32(0, true);
    const cbKey = dv.getUint32(4, true);
    if (magic !== 0x31534345 || cbKey !== 32 || pubKeyBuf.length < 8 + cbKey * 2) {
      return {
        seedValid: false,
        payloadValid: false,
        reason: `bad BCRYPT_ECCPUBLIC_BLOB header magic=0x${magic.toString(16)} cbKey=${cbKey} len=${pubKeyBuf.length}`,
      };
    }

    // .slice() preserves the buffer-kind generic, so x/y are also Uint8Array<ArrayBuffer>.
    const x = pubKeyBuf.slice(8, 8 + cbKey);
    const y = pubKeyBuf.slice(8 + cbKey, 8 + cbKey * 2);

    const cryptoKey = await crypto.subtle.importKey(
      'jwk',
      { kty: 'EC', crv: 'P-256', x: bytesToB64Url(x), y: bytesToB64Url(y) },
      { name: 'ECDSA', namedCurve: 'P-256' },
      false,
      ['verify'],
    );

    const seedSigBytes = b64ToBytes(attestationData.seedSignature);
    const seedDataBytes = b64ToBytes(attestationData.machineId);
    const seedValid = await crypto.subtle.verify(
      { name: 'ECDSA', hash: 'SHA-256' },
      cryptoKey,
      seedSigBytes,
      seedDataBytes,
    );

    // Canonicalize the payload exactly the way the realtime-api server does
    // (see `canonicalJson` in managers/realtime/attestation.ts) instead of
    // hard-coding a field list. This verifies the current three-field payload
    // ({ clerkUserId, machineId, nonce }) and still reproduces the exact bytes
    // for any legacy payload that additionally carries `timestamp`, matching
    // the shared server-side verification path.
    const canonicalPayload = canonicalizeAttestationPayload(
      attestationData.attestationPayload,
    );
    const payloadSigBytes = b64ToBytes(attestationData.attestationSignature);
    const payloadDataBytes = new TextEncoder().encode(canonicalPayload);
    const payloadValid = await crypto.subtle.verify(
      { name: 'ECDSA', hash: 'SHA-256' },
      cryptoKey,
      payloadSigBytes,
      payloadDataBytes,
    );

    console.log('[ATTESTATION_DIAGNOSTIC] canonical payload string:', canonicalPayload);
    console.log('[ATTESTATION_DIAGNOSTIC] seed sig length (bytes):', seedSigBytes.length, '(expect 64 for ECDSA P-256 IEEE P1363)');
    console.log('[ATTESTATION_DIAGNOSTIC] payload sig length (bytes):', payloadSigBytes.length, '(expect 64 for ECDSA P-256 IEEE P1363)');

    return { seedValid, payloadValid };
  } catch (e) {
    return {
      seedValid: false,
      payloadValid: false,
      reason: `local verify threw: ${(e as Error)?.message ?? String(e)}`,
    };
  }
}

/**
 * Submit attestation to backend
 */
export async function submitAttestation(
  attestationData: AttestationData,
  clerkToken: string,
): Promise<AttestationResult> {
  const url = `${ensureTrailingSlash(constants.REALTIME_API_BASE_URL)}realtime/machine-identity/attest`;

  // TEMP DIAGNOSTIC (#12941): dump the exact body the server will receive plus a
  // local verification result so we can tell deployment issues from data issues.
  const requestBody = JSON.stringify(attestationData);
  const localVerify = await diagnosticVerifyAttestationLocally(attestationData);
  console.log('[ATTESTATION_DIAGNOSTIC] POST', url);
  console.log('[ATTESTATION_DIAGNOSTIC] body bytes:', requestBody.length);
  console.log('[ATTESTATION_DIAGNOSTIC] body:', requestBody);
  console.log('[ATTESTATION_DIAGNOSTIC] local verify:', localVerify);

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${clerkToken}`,
      },
      body: requestBody,
    });

    // Check HTTP status before parsing response
    // The backend may return JSON even on error status codes, but we should validate
    if (!response.ok) {
      // Try to extract error message from response body if available
      // The backend typically returns JSON with error details even on HTTP errors
      let errorMessage = `Failed to submit attestation: ${response.status} ${response.statusText}`;
      let errorData: AttestationResult | null = null;
      
      try {
        const text = await response.text();
        if (text) {
          errorData = JSON.parse(text) as AttestationResult;
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          }
        }
      } catch {
        // If JSON parsing fails, use the default error message
        // This can happen if backend returns non-JSON error (e.g., HTML error page)
      }
      
      // Throw error with details from JSON if available, or fallback to HTTP status
      const error = new Error(errorMessage);
      if (errorData) {
        // Include error details in the thrown error for better error handling
        (error as any).attestationResult = errorData;
      }
      throw error;
    }

    // Response is OK - safe to parse JSON
    const data = await response.json() as AttestationResult;

    if (!data.success) {
      console.warn('[MachineIdAttestation] Attestation failed:', data);
      Sentry.captureMessage('Machine ID attestation failed', {
        level: 'warning',
        tags: {
          service: 'machineIdAttestation',
          operation: 'submitAttestation',
          error: data.error,
        },
        contexts: {
          attestation: {
            error: data.error,
            message: data.message,
          },
        },
      });
    } else {
      const fmtDate = (v: number | undefined, fallback: string) => {
        if (!v) return fallback;
        try { const d = new Date(v); return isNaN(d.getTime()) ? fallback : d.toISOString(); } catch { return fallback; }
      };
      console.log('[MachineIdAttestation] Attestation successful:', {
        attestedAt: fmtDate(data.attestedAt, 'unknown'),
        expiresAt: fmtDate(data.expiresAt, 'never'),
      });
    }

    return data;
  } catch (error) {
    console.error('[MachineIdAttestation] Failed to submit attestation:', error);
    Sentry.captureException(error, {
      tags: {
        service: 'machineIdAttestation',
        operation: 'submitAttestation',
      },
    });
    throw error;
  }
}

/**
 * Complete attestation flow
 * 
 * @param clerkUserId - Clerk user ID
 * @param clerkToken - Clerk authentication token
 * @param getMachineIdAttestationFn - Function to call C++ to get attestation data
 * @returns Attestation result
 */
export async function performAttestation(
  clerkUserId: string,
  clerkToken: string,
  getMachineIdAttestationFn: (nonce: string, clerkUserId: string) => Promise<AttestationData>,
): Promise<AttestationResult> {
  try {
    console.log('[MachineIdAttestation] Starting attestation flow for user:', clerkUserId);

    // Step 1: Get challenge nonce from backend
    const challenge = await getChallenge();

    // Step 2: Call C++ to generate TPM signatures
    console.log('[MachineIdAttestation] Requesting attestation data from C++');
    const attestationData = await getMachineIdAttestationFn(challenge.nonce, clerkUserId);

    // Validate that the attestation data is complete
    if (
      !attestationData.machineId ||
      !attestationData.aikPublicKey ||
      !attestationData.seedSignature ||
      !attestationData.attestationSignature ||
      !attestationData.attestationPayload
    ) {
      throw new Error('Incomplete attestation data received from C++');
    }

    console.log('[MachineIdAttestation] Attestation data received from C++:', {
      machineId: attestationData.machineId.substring(0, 16) + '...',
      aikPublicKeyLength: attestationData.aikPublicKey.length,
      seedSignatureLength: attestationData.seedSignature.length,
      attestationSignatureLength: attestationData.attestationSignature.length,
    });

    // Step 3: Submit attestation to backend
    const result = await submitAttestation(attestationData, clerkToken);

    if (result.success) {
      console.log('[MachineIdAttestation] Attestation flow completed successfully');
      Sentry.addBreadcrumb({
        category: 'machineIdAttestation',
        message: 'Attestation successful',
        level: 'info',
        data: {
          clerkUserId,
          attestedAt: result.attestedAt,
        },
      });
    }

    return result;
  } catch (error) {
    console.error('[MachineIdAttestation] Attestation flow failed:', error);
    Sentry.captureException(error, {
      tags: {
        service: 'machineIdAttestation',
        operation: 'performAttestation',
      },
      contexts: {
        user: {
          clerkUserId,
        },
      },
    });
    throw error;
  }
}

/**
 * Check if we need to re-attest
 * 
 * This should be called after login to determine if attestation is needed.
 * The C++ code maintains attestation metadata in the registry.
 * 
 * @param clerkUserId - Current Clerk user ID
 * @param checkValidationFn - Function to call C++ to check attestation validity
 * @returns true if attestation is needed, false if already valid
 */
export async function needsAttestation(
  clerkUserId: string,
  checkValidationFn: (clerkUserId: string) => Promise<AttestationValidation>,
): Promise<boolean> {
  try {
    const validation = await checkValidationFn(clerkUserId);

    if (validation.isValid) {
      let attestedAtStr = 'unknown';
      try {
        if (validation.attestedAt) {
          const d = new Date(validation.attestedAt);
          if (!isNaN(d.getTime())) attestedAtStr = d.toISOString();
        }
      } catch { /* safe to ignore — only affects log output */ }

      console.log('[MachineIdAttestation] Existing attestation is valid:', {
        attestedUserId: validation.attestedUserId,
        attestedAt: attestedAtStr,
      });
      return false;
    }

    console.log('[MachineIdAttestation] Attestation needed:', validation.reason);
    return true;
  } catch (error) {
    console.error('[MachineIdAttestation] Failed to check attestation validity:', error);
    return true;
  }
}

